import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { FaPrint } from "react-icons/fa";
import { getData } from "../services/FetchNodeservices";

// ✅ Convert number to words (Indian numbering system)
function numberToWords(num) {
  const a = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
    "Seventeen", "Eighteen", "Nineteen"
  ];
  const b = [
    "", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"
  ];

  if ((num = num.toString()).length > 9) return "Overflow";
  const n = ("000000000" + num)
    .substr(-9)
    .match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);

  if (!n) return "";
  let str = "";
  if (Number(n[1]) !== 0) str += (a[Number(n[1])] || b[n[1][0]] + " " + a[n[1][1]]) + " Crore ";
  if (Number(n[2]) !== 0) str += (a[Number(n[2])] || b[n[2][0]] + " " + a[n[2][1]]) + " Lakh ";
  if (Number(n[3]) !== 0) str += (a[Number(n[3])] || b[n[3][0]] + " " + a[n[3][1]]) + " Thousand ";
  if (Number(n[4]) !== 0) str += (a[Number(n[4])] || b[n[4][0]] + " " + a[n[4][1]]) + " Hundred ";
  if (Number(n[5]) !== 0) str += (str !== "" ? "and " : "") + (a[Number(n[5])] || b[n[5][0]] + " " + a[n[5][1]]) + " ";
  return str.trim() + " Only";
}

export default function ViewInvoice() {
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const companyState = "Madhya Pradesh";

  const fetchInvoiceData = useCallback(async () => {
    try {
      const invoiceRes = await getData(`api/invoice_master/${id}`);
      let invoiceData = invoiceRes?.data || invoiceRes;

      const itemRes = await getData(`api/invoice_items?invoice_id=${id}`);
      const fetchedItems = Array.isArray(itemRes?.data)
        ? itemRes.data
        : Array.isArray(itemRes)
        ? itemRes
        : [];

      const isInterState =
        invoiceData.state?.trim().toLowerCase() !==
        companyState.trim().toLowerCase();

      const updatedItems = fetchedItems.map((item) => {
        const taxable = parseFloat(item.taxable_amount || 0);
        const rate = 0.18;
        let cgst = 0, sgst = 0, igst = 0;

        if (isInterState) igst = taxable * rate;
        else {
          cgst = taxable * 0.09;
          sgst = taxable * 0.09;
        }

        return {
          ...item,
          cgst: cgst.toFixed(2),
          sgst: sgst.toFixed(2),
          igst: igst.toFixed(2),
        };
      });

      const totalTaxable = updatedItems.reduce(
        (sum, i) => sum + parseFloat(i.taxable_amount || 0),
        0
      );
      const totalCGST = updatedItems.reduce(
        (sum, i) => sum + parseFloat(i.cgst || 0),
        0
      );
      const totalSGST = updatedItems.reduce(
        (sum, i) => sum + parseFloat(i.sgst || 0),
        0
      );
      const totalIGST = updatedItems.reduce(
        (sum, i) => sum + parseFloat(i.igst || 0),
        0
      );

      const totalAmount = totalTaxable + totalCGST + totalSGST + totalIGST;

      setInvoice({
        ...invoiceData,
        taxable_amount: totalTaxable.toFixed(2),
        cgst: totalCGST.toFixed(2),
        sgst: totalSGST.toFixed(2),
        igst: totalIGST.toFixed(2),
        total_amount: totalAmount.toFixed(2),
        isInterState,
      });

      setItems(updatedItems);
    } catch (e) {
      console.error("Error fetching invoice:", e);
    } finally {
      setLoading(false);
    }
  }, [id, companyState]);

  useEffect(() => {
    fetchInvoiceData();
  }, [fetchInvoiceData]);

  const handlePrint = () => window.print();

  if (loading) return <p className="text-center py-10">Loading...</p>;
  if (!invoice) return <p className="text-center py-10">Invoice not found.</p>;

  const amountInWords = numberToWords(Math.floor(invoice.total_amount || 0));

  return (
    <>
      {/* ================================================================== */}
      {/*                       SCREEN VIEW (WEB UI)                         */}
      {/* ================================================================== */}
      <div className="bg-white min-h-screen p-8 text-sm font-sans text-gray-800 print:hidden">
        
        {/* Top Action Bar */}
        <div className="flex justify-between items-center mb-6 border-b pb-4">
          <h1 className="text-xl text-gray-500">Invoice</h1>
          <button onClick={handlePrint} className="flex items-center gap-2 text-gray-500 hover:text-black">
             <FaPrint />
          </button>
        </div>

        {/* Header Title */}
        <div className="text-center mb-10">
           <h2 className="text-3xl font-normal text-gray-800">Tax Invoice</h2>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-2 gap-12 mb-8">
           {/* Left Info */}
           <div className="space-y-1">
              <div className="flex"><span className="font-bold w-40">Invoice No:</span> <span>{invoice.invoice_number}</span></div>
              <div className="flex"><span className="font-bold w-40">Invoice Date:</span> <span>{invoice.invoice_date}</span></div>
              <div className="flex"><span className="font-bold w-40">Reverse Charge(Y/N):</span> <span>N</span></div>
              <div className="flex"><span className="font-bold w-40">State:</span> <span>{companyState}</span></div>
           </div>
           {/* Right Info */}
           <div className="space-y-1">
              <div className="flex"><span className="font-bold w-40">Date of Supply:</span> <span>{invoice.invoice_date}</span></div>
              <div className="flex"><span className="font-bold w-40">Place of Supply:</span> <span>{invoice.state}</span></div>
              <div className="flex"><span className="font-bold w-40">Notes:</span> <span></span></div>
           </div>
        </div>

        {/* Recipient */}
        <div className="mb-8 space-y-1">
           <div className="font-bold mb-2">Details of Service Recipient:-</div>
           <div className="flex"><span className="font-bold w-40">Name:</span> <span className="font-bold text-gray-900">{invoice.name}</span></div>
           <div className="flex"><span className="font-bold w-40">GSTIN:</span> <span className="font-bold text-gray-900">{invoice.gstin}</span></div>
           <div className="flex"><span className="font-bold w-40">State:</span> <span>{invoice.state}</span></div>
           <div className="flex"><span className="font-bold w-40">Address:</span> <span>{invoice.company_address}</span></div>
        </div>

        {/* Table */}
        <div className="mb-4">
           <table className="w-full text-left">
              <thead className="bg-white border-b border-gray-100 text-xs uppercase text-gray-600 font-bold">
                 <tr>
                    <th className="py-3">S.No.</th>
                    <th className="py-3">Service Description</th>
                    <th className="py-3">SAC Code</th>
                    <th className="py-3">Taxable Value</th>
                    <th className="py-3">CGST</th>
                    <th className="py-3">SGST</th>
                    <th className="py-3">IGST</th>
                    <th className="py-3 text-right">Total</th>
                 </tr>
              </thead>
              <tbody className="text-gray-700">
                {items.map((item, i) => (
                  <tr key={i} className="border-b border-gray-100">
                     <td className="py-3">{i+1}</td>
                     <td className="py-3">{item.description}</td>
                     <td className="py-3">{item.sac_code}</td>
                     <td className="py-3">{item.taxable_amount}</td>
                     <td className="py-3">{parseFloat(item.cgst) > 0 ? item.cgst : "0"}</td>
                     <td className="py-3">{parseFloat(item.sgst) > 0 ? item.sgst : "0"}</td>
                     <td className="py-3">{parseFloat(item.igst) > 0 ? `${item.igst} (@18%)` : "0"}</td>
                     <td className="py-3 text-right">
                        {(parseFloat(item.taxable_amount) + parseFloat(item.cgst) + parseFloat(item.sgst) + parseFloat(item.igst)).toFixed(2)}
                     </td>
                  </tr>
                ))}
                {/* Total Row */}
                <tr className="bg-gray-50 font-bold">
                    <td colSpan={3} className="py-2 text-right pr-10">Total</td>
                    <td className="py-2">{invoice.taxable_amount}</td>
                    <td className="py-2">{parseFloat(invoice.cgst) > 0 ? invoice.cgst : "0"}</td>
                    <td className="py-2">{parseFloat(invoice.sgst) > 0 ? invoice.sgst : "0"}</td>
                    <td className="py-2">{parseFloat(invoice.igst) > 0 ? invoice.igst : "0"}</td>
                    <td className="py-2 text-right">{invoice.total_amount}</td>
                </tr>
              </tbody>
           </table>
        </div>

        {/* Words & Calculation */}
        <div className="flex mb-8">
           {/* Left: Words */}
           <div className="w-3/5 pt-2">
              <div className="bg-gray-50 py-1 px-2 text-gray-600 text-xs font-bold mb-2">Total invoice amount in words</div>
              <div className="px-2 font-bold text-sm">Rupees: {amountInWords}</div>
           </div>
           
           {/* Right: Calculation */}
           <div className="w-2/5">
              <div className="bg-gray-50 py-1 px-2 flex justify-between font-bold text-xs text-gray-600">
                 <span>Total Amount before Tax</span>
                 <span>{invoice.taxable_amount}</span>
              </div>
              <div className="py-1 px-2 flex justify-between text-sm mt-1">
                 <span className="font-bold">Add: CGST</span>
                 <span>{parseFloat(invoice.cgst) > 0 ? invoice.cgst : "0"}</span>
              </div>
              <div className="py-1 px-2 flex justify-between text-sm">
                 <span className="font-bold">Add: SGST</span>
                 <span>{parseFloat(invoice.sgst) > 0 ? invoice.sgst : "0"}</span>
              </div>
              <div className="py-1 px-2 flex justify-between text-sm">
                 <span className="font-bold">Add: IGST</span>
                 <span>{parseFloat(invoice.igst) > 0 ? invoice.igst : "0"}</span>
              </div>
              <div className="py-1 px-2 flex justify-between text-sm font-bold">
                 <span>Total Tax Amount</span>
                 <span>{(parseFloat(invoice.cgst) + parseFloat(invoice.sgst) + parseFloat(invoice.igst)).toFixed(2)}</span>
              </div>
              <div className="bg-gray-50 py-1 px-2 flex justify-between text-sm font-bold mt-1">
                 <span>Total Amount after Tax</span>
                 <span>{invoice.total_amount}</span>
              </div>
           </div>
        </div>

        {/* Footer Grid */}
        <div className="flex gap-8 border-t border-gray-100 pt-4">
           {/* Left: Bank */}
           <div className="w-1/2">
              <div className="bg-gray-50 py-1 px-2 font-bold text-gray-600 text-xs mb-2">Bank Details</div>
              <div className="space-y-1 text-sm px-2 font-bold text-gray-800">
                 <div>Bank A/C No.: 50200003370599</div>
                 <div>Bank IFSC: HDFC0002744</div>
                 <div>Bank Name: HDFC Bank</div>
                 <div>PAN No.: AAICM4999F</div>
              </div>
              <div className="mt-8 px-2 font-bold text-sm">Terms & Conditions</div>
           </div>

           {/* Right: Signatory */}
           <div className="w-1/2 flex flex-col justify-between">
               <div>
                  <div className="bg-gray-50 py-1 px-2 font-bold text-gray-600 text-xs mb-2">GST on Reverse Charge</div>
                  <div className="text-right mt-8 text-sm text-gray-700">
                     For- Morgan Placement & Consultancy Pvt. Ltd.
                  </div>
               </div>
           </div>
        </div>

        {/* Bottom Signatures */}
        <div className="flex justify-between mt-16 px-20 text-xs text-gray-500">
           <div>Common Seal</div>
           <div>Authorized Signatory</div>
        </div>

      </div>


      {/* ================================================================== */}
      {/*                       PRINT VIEW (STRICT LAYOUT)                   */}
      {/* ================================================================== */}
      <div className="hidden print:block bg-white text-xs font-sans text-black leading-tight w-full">
        <style>
          {`
            @page { size: A4; margin: 0; }
            @media print {
              body { -webkit-print-color-adjust: exact; }
            }
          `}
        </style>

        {/* 1. TOP WHITE SPACE (Letterhead Area) */}
        <div className="h-24 w-full bg-white"></div>

        {/* 2. MAIN INVOICE BORDER BOX */}
        <div className="border-2 border-black mx-8">
            
            {/* HEADER */}
            <div className="text-center font-bold text-lg border-b border-black py-1">
              Tax Invoice
            </div>

            {/* TOP GRID DETAILS */}
            <div className="grid grid-cols-2 border-b border-black">
              {/* Left Column */}
              <div className="border-r border-black">
                <div className="flex border-b border-black">
                  <span className="w-32 font-bold pl-1 py-1 border-r border-black">Invoice No:</span>
                  <span className="pl-2 py-1">{invoice.invoice_number}</span>
                </div>
                <div className="flex border-b border-black">
                  <span className="w-32 font-bold pl-1 py-1 border-r border-black">Invoice Date:</span>
                  <span className="pl-2 py-1">{invoice.invoice_date}</span>
                </div>
                <div className="flex border-b border-black">
                  <span className="w-32 font-bold pl-1 py-1 border-r border-black">Reverse Charge(Y/N):</span>
                  <span className="pl-2 py-1">N</span>
                </div>
                <div className="flex">
                  <span className="w-32 font-bold pl-1 py-1 border-r border-black">State:</span>
                  <span className="pl-2 py-1">{companyState}</span>
                </div>
              </div>
              {/* Right Column */}
              <div>
                <div className="flex border-b border-black">
                  <span className="w-32 font-bold pl-1 py-1 border-r border-black">Date of Supply:</span>
                  <span className="pl-2 py-1">{invoice.invoice_date}</span>
                </div>
                <div className="flex border-b border-black">
                  <span className="w-32 font-bold pl-1 py-1 border-r border-black">Place of Supply:</span>
                  <span className="pl-2 py-1">{invoice.state}</span>
                </div>
                <div className="flex border-b border-black">
                  <span className="w-32 font-bold pl-1 py-1 border-r border-black">GSTIN:</span>
                  <span className="pl-2 py-1">23AAICM4999F1Z4</span>
                </div>
                <div className="flex">
                  <span className="w-32 font-bold pl-1 py-1 border-r border-black">Currency:</span>
                  <span className="pl-2 py-1">INR</span>
                </div>
              </div>
            </div>

            {/* EMPTY ROW (Spacer) */}
            <div className="border-b border-black h-6"></div>

            {/* RECIPIENT DETAILS */}
            <div className="border-b border-black">
              <div className="font-bold pl-1 py-1 border-b border-black">Details of Service Recipient:-</div>
              <div className="flex border-b border-black">
                <div className="flex-1 border-r border-black pl-1 py-1 flex">
                  <span className="font-bold mr-1">Name:</span> {invoice.name}
                </div>
                <div className="w-1/4 border-r border-black pl-1 py-1 flex">
                  <span className="font-bold mr-1">GSTIN:</span> {invoice.gstin}
                </div>
                <div className="w-1/4 pl-1 py-1 flex">
                  <span className="font-bold mr-1">State:</span> {invoice.state}
                </div>
              </div>
              <div className="pl-1 py-1">
                <span className="font-bold mr-1">Address:</span> {invoice.company_address}
              </div>
            </div>

            {/* ITEMS TABLE */}
            <div className="w-full">
                <div className="flex font-bold text-center border-b border-black">
                    <div className="w-[5%] border-r border-black py-1">S.No.</div>
                    <div className="w-[35%] border-r border-black py-1 text-left pl-1">Service Description</div>
                    <div className="w-[10%] border-r border-black py-1">SAC Code</div>
                    <div className="w-[10%] border-r border-black py-1 text-left pl-1">Taxable Value</div>
                    <div className="w-[10%] border-r border-black py-1 text-left pl-1">CGST</div>
                    <div className="w-[10%] border-r border-black py-1 text-left pl-1">SGST</div>
                    <div className="w-[10%] border-r border-black py-1 text-left pl-1">IGST</div>
                    <div className="w-[10%] py-1 text-left pl-1">Total</div>
                </div>

                {items.map((item, i) => (
                  <div key={i} className="flex text-left border-b border-black">
                    <div className="w-[5%] border-r border-black py-2 text-center">{i + 1}</div>
                    <div className="w-[35%] border-r border-black py-2 pl-1">{item.description}</div>
                    <div className="w-[10%] border-r border-black py-2 pl-1">{item.sac_code}</div>
                    <div className="w-[10%] border-r border-black py-2 pl-1">{item.taxable_amount}</div>
                    <div className="w-[10%] border-r border-black py-2 pl-1">{parseFloat(item.cgst) > 0 ? item.cgst : "0"}</div>
                    <div className="w-[10%] border-r border-black py-2 pl-1">{parseFloat(item.sgst) > 0 ? item.sgst : "0"}</div>
                    <div className="w-[10%] border-r border-black py-2 pl-1">
                      {parseFloat(item.igst) > 0 ? `${item.igst} (@18%)` : "0"}
                    </div>
                    <div className="w-[10%] py-2 pl-1">
                      {(parseFloat(item.taxable_amount) + parseFloat(item.cgst) + parseFloat(item.sgst) + parseFloat(item.igst)).toFixed(2)}
                    </div>
                  </div>
                ))}

                {/* TOTAL ROW */}
                <div className="flex font-bold border-b border-black">
                    <div className="w-[50%] border-r border-black py-1 text-center text-xl">Total</div>
                    <div className="w-[10%] border-r border-black py-1 pl-1">{invoice.taxable_amount}</div>
                    <div className="w-[10%] border-r border-black py-1 pl-1">{parseFloat(invoice.cgst) > 0 ? invoice.cgst : "0"}</div>
                    <div className="w-[10%] border-r border-black py-1 pl-1">{parseFloat(invoice.sgst) > 0 ? invoice.sgst : "0"}</div>
                    <div className="w-[10%] border-r border-black py-1 pl-1">{parseFloat(invoice.igst) > 0 ? invoice.igst : "0"}</div>
                    <div className="w-[10%] py-1 pl-1">{invoice.total_amount}</div>
                </div>
            </div>

            {/* WORDS & CALCULATIONS */}
            <div className="flex border-b border-black">
                {/* Left: Words */}
                <div className="w-[60%] border-r border-black flex flex-col">
                    <div className="border-b border-black text-center font-bold py-1">Total invoice amount in words</div>
                    <div className="flex-1 flex items-center justify-center p-4 text-center font-bold">
                       Rupees: {amountInWords}
                    </div>
                </div>

                {/* Right: Calculation */}
                <div className="w-[40%]">
                    <div className="flex justify-between border-b border-black pl-1 pr-1 py-1">
                        <span className="font-bold">Total Amount before Tax</span>
                        <span>{invoice.taxable_amount}</span>
                    </div>
                    <div className="flex justify-between border-b border-black pl-1 pr-1 py-1">
                        <span className="font-bold">Add: CGST</span>
                        <span>{parseFloat(invoice.cgst) > 0 ? invoice.cgst : "0"}</span>
                    </div>
                    <div className="flex justify-between border-b border-black pl-1 pr-1 py-1">
                        <span className="font-bold">Add: SGST</span>
                        <span>{parseFloat(invoice.sgst) > 0 ? invoice.sgst : "0"}</span>
                    </div>
                    <div className="flex justify-between border-b border-black pl-1 pr-1 py-1">
                        <span className="font-bold">Add: IGST</span>
                        <span>{parseFloat(invoice.igst) > 0 ? invoice.igst : "0"}</span>
                    </div>
                    <div className="flex justify-between border-b border-black pl-1 pr-1 py-1">
                        <span className="font-bold">Total Tax Amount</span>
                        <span>{(parseFloat(invoice.cgst) + parseFloat(invoice.sgst) + parseFloat(invoice.igst)).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between pl-1 pr-1 py-1 font-bold">
                        <span>Total Amount after Tax</span>
                        <span>{invoice.total_amount}</span>
                    </div>
                </div>
            </div>

            {/* FOOTER SECTION */}
            <div className="flex border-b border-black">
                {/* Left Column: Bank & Terms */}
                <div className="w-[50%] border-r border-black flex flex-col">
                    <div className="border-b border-black font-bold text-center py-1">Bank Details</div>
                    <div className="pl-1 py-1 border-b border-black font-bold">Bank A/C No.: 50200003370599</div>
                    <div className="pl-1 py-1 border-b border-black font-bold">Bank IFSC: HDFC0002744</div>
                    <div className="pl-1 py-1 border-b border-black font-bold">Bank Name: HDFC Bank</div>
                    <div className="pl-1 py-1 border-b border-black font-bold">PAN No.: AAICM4999F</div>
                    
                    <div className="flex-1 pt-4 pl-1">
                        <div className="font-bold text-center">Terms & Conditions</div>
                    </div>
                </div>

                {/* Right Column: Signatory Header */}
                <div className="w-[50%] flex flex-col">
                    <div className="border-b border-black flex">
                        <div className="w-full pl-1 py-1 font-bold">GST on Reverse Charge</div>
                    </div>
                    
                    <div className="flex-1 pt-8 text-center">
                        For- Morgan Placement & Consultancy Pvt. Ltd.
                    </div>
                </div>
            </div>

            {/* BOTTOM ROW: SEAL & SIGNATORY */}
            <div className="flex h-12">
               <div className="w-[50%] border-r border-black flex items-end justify-center pb-1">
                   Common Seal
               </div>
               <div className="w-[50%] flex items-end justify-center pb-1">
                   Authorized Signatory
               </div>
            </div>
        </div>
        {/* 3. BOTTOM WHITE SPACE */}
        <div className="h-20 w-full bg-white"></div>
      </div>
    </>
  );
}